import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { ArrowRight, Shield, Zap, Coins, ChevronDown, Pickaxe, Rocket, CreditCard, Building2, Loader2, Bitcoin, Info, Layers } from 'lucide-react'
import { PoweredBySlider } from '../components/landing'
import { api } from '../services/api'
import { useConfig } from '../contexts/ConfigContext'

// Brand colors from brandbook
const GOLD = '#EFCE92'
const BLACK = '#0B0B0C'

export default function LandingPage() {
  const { t } = useTranslation(['landing', 'common'])
  const [expandedBlock, setExpandedBlock] = useState<number | null>(null)
  const [expandedInfoCards, setExpandedInfoCards] = useState<Set<number>>(new Set())
  const config = useConfig()

  const toggleInfoCard = (index: number) => {
    setExpandedInfoCards(prev => {
      const newSet = new Set(prev)
      if (newSet.has(index)) {
        newSet.delete(index)
      } else {
        newSet.add(index)
      }
      return newSet
    })
  }

  // Fetch vaults from API - use isActive=true for consistency with /app/vaults
  const { data: vaultsData, isLoading: vaultsLoading } = useQuery({
    queryKey: ['vaults', { isActive: true }],
    queryFn: () => api.getVaults({ isActive: true }),
    staleTime: 5 * 60 * 1000,
  })

  // Fetch supply breakdown for Bitcoin vault progress
  const { data: supplyData } = useQuery({
    queryKey: ['supply'],
    queryFn: () => api.getSupplyBreakdown(),
    staleTime: 60 * 1000, // Refresh every minute
  })

  const vaults = vaultsData?.data || []
  const miningProgress = supplyData?.data?.percentCirculating || 0
  const bitcoinVaultProgress = supplyData?.data?.bitcoinVault?.progress || 0

  // Get Treasury vault stats (v3.0: Treasury vaults)
  const getTreasuryStats = () => {
    const treasuryVaults = vaults.filter((v: any) => !v.isComingSoon)
    if (treasuryVaults.length === 0) return null

    const maxBaseAPY = Math.max(...treasuryVaults.map((v: any) => v.maxAPY || v.baseAPY || 0))
    // Use dynamicTakaraAPY if available, fallback to maxTakaraAPY
    const maxTakaraAPY = Math.max(...treasuryVaults.map((v: any) => v.dynamicTakaraAPY || v.maxTakaraAPY || v.baseTakaraAPY || 0))
    const minDeposit = Math.min(...treasuryVaults.map((v: any) => v.minInvestment || 0))

    // Calculate max total returns (APY * duration / 12)
    const maxTotalReturns = Math.max(...treasuryVaults.map((v: any) => {
      const apy = v.maxAPY || v.baseAPY || 0
      const duration = v.duration || 12
      return Math.round(apy * duration / 12)
    }))

    // Get vault durations
    const durations = [...new Set(treasuryVaults.map((v: any) => v.duration))].sort((a, b) => a - b)

    return { maxBaseAPY, maxTakaraAPY, minDeposit, maxTotalReturns, durations }
  }

  const treasuryStats = getTreasuryStats()

  const toggleBlock = (blockId: number) => {
    setExpandedBlock(expandedBlock === blockId ? null : blockId)
  }

  return (
    <div className="relative">
      {/* Solana Ecosystem Badge */}
      <div className="bg-navy-800 py-2 px-4 text-center border-b border-gold-300/20">
        <p style={{ color: GOLD }} className="font-medium text-sm sm:text-base flex items-center justify-center gap-2">
          <span className="px-2 py-0.5 bg-gold-300/10 rounded text-xs uppercase tracking-wider">{t('landing:ecosystem.badge')}</span>
          <span className="hidden sm:inline">|</span>
          <span className="hidden sm:inline">{t('landing:ecosystem.totalSupply', { supply: config.tokenomics.totalSupply.toLocaleString() })}</span>
        </p>
      </div>

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-navy-900 min-h-[70vh] sm:min-h-[80vh] flex items-center">
        {/* Japanese Background Patterns */}
        <div className="absolute inset-0 zen-circles"></div>
        <div className="absolute inset-0 seigaiha-pattern opacity-50"></div>

        {/* Gold Tree - Animated */}
        <div className="absolute top-10 right-0 w-[500px] h-[500px] opacity-30 animate-float hidden lg:block">
          <img src="/images/brand/gold-tree.png" alt="" className="w-full h-full object-contain drop-shadow-2xl" />
        </div>

        {/* Floating Gold Coins */}
        <div className="absolute top-[15%] left-[5%] w-24 h-24 opacity-40 animate-float hidden md:block" style={{ animationDelay: '0s', animationDuration: '6s' }}>
          <img src="/images/brand/gold-coin.png" alt="" className="w-full h-full object-contain" />
        </div>
        <div className="absolute top-[60%] left-[8%] w-16 h-16 opacity-30 animate-float hidden md:block" style={{ animationDelay: '2s', animationDuration: '8s' }}>
          <img src="/images/brand/gold-coin.png" alt="" className="w-full h-full object-contain" />
        </div>
        {/* Abstract Gold Shape */}
        <div className="absolute bottom-10 left-[10%] w-48 h-48 opacity-15 animate-spin-slow hidden lg:block">
          <img src="/images/brand/gold-abstract.png" alt="" className="w-full h-full object-contain" />
        </div>

        {/* Decorative gold circles with blur */}
        <div className="absolute top-10 left-10 w-64 h-64 rounded-full opacity-20 blur-3xl" style={{ background: GOLD }}></div>
        <div className="absolute bottom-20 left-1/4 w-96 h-96 rounded-full opacity-15 blur-3xl" style={{ background: GOLD }}></div>
        <div className="absolute top-1/3 right-1/4 w-48 h-48 rounded-full opacity-10 blur-2xl" style={{ background: GOLD }}></div>

        {/* Animated floating particles */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(15)].map((_, i) => (
            <div
              key={i}
              className="absolute rounded-full animate-float-particle"
              style={{
                width: `${4 + (i % 5) * 3}px`,
                height: `${4 + (i % 5) * 3}px`,
                background: GOLD,
                opacity: 0.3 + (i % 3) * 0.1,
                top: `${5 + (i * 6) % 90}%`,
                left: `${3 + (i * 7) % 94}%`,
                animationDelay: `${i * 0.4}s`,
                animationDuration: `${4 + (i % 4) * 2}s`
              }}
            />
          ))}
        </div>

        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6 sm:py-10 lg:py-32">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Left Content */}
            <div className="space-y-6 sm:space-y-8">
              {/* Logo */}
              <div className="flex items-center">
                <img
                  src="/images/brand/logo-takara.png"
                  alt="Takara"
                  className="h-12 sm:h-16 lg:h-20 w-auto"
                />
              </div>

              <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold tracking-tight text-white leading-tight">
                {t('landing:hero.title')}{' '}
                <br className="hidden sm:block" />
                <span style={{ color: GOLD }}>{t('landing:hero.titleHighlight')}</span>
              </h1>

              <p className="text-base sm:text-lg lg:text-xl text-gray-300 leading-relaxed">
                {t('landing:hero.subtitle')}
              </p>

              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                <Link
                  to="/app/vaults"
                  className="btn-gold inline-flex items-center justify-center gap-2 px-6 sm:px-8 py-3 sm:py-4 rounded-lg font-semibold text-base sm:text-lg"
                >
                  {t('landing:hero.startEarning')}
                  <ArrowRight className="h-5 w-5" />
                </Link>
                <Link
                  to="/app/faq"
                  className="btn-outline-gold inline-flex items-center justify-center gap-2 px-6 sm:px-8 py-3 sm:py-4 rounded-lg font-semibold text-base sm:text-lg"
                >
                  {t('landing:hero.learnMore')}
                </Link>
              </div>
            </div>
          </div>

          {/* Stats Row - Dynamic from API */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 mt-10 sm:mt-16 pt-6 sm:pt-8 border-t border-gold-300/20">
            <div className="text-center">
              <div className="text-xl sm:text-3xl lg:text-4xl font-bold" style={{ color: GOLD }}>
                {t('landing:stats.upTo', { value: treasuryStats?.maxBaseAPY || 19.2 })}
              </div>
              <div className="text-xs sm:text-sm text-gray-400 mt-1">{t('landing:stats.usdtAPY')}</div>
            </div>
            <div className="text-center">
              <div className="text-xl sm:text-3xl lg:text-4xl font-bold" style={{ color: GOLD }}>
                {t('landing:stats.upTo', { value: treasuryStats?.maxTotalReturns || 57 })}
              </div>
              <div className="text-xs sm:text-sm text-gray-400 mt-1">{t('landing:stats.totalReturns')}</div>
            </div>
            <div className="text-center">
              <div className="text-xl sm:text-3xl lg:text-4xl font-bold" style={{ color: GOLD }}>
                {t('landing:stats.dynamic')}
              </div>
              <div className="text-xs sm:text-sm text-gray-400 mt-1">{t('landing:stats.takaraAPY')}</div>
            </div>
            <div className="text-center">
              <div className="text-xl sm:text-3xl lg:text-4xl font-bold" style={{ color: GOLD }}>{t('landing:stats.monthly')}</div>
              <div className="text-xs sm:text-sm text-gray-400 mt-1">{t('landing:stats.payouts')}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Benefits Section */}
      <section className="py-6 sm:py-10 bg-navy-800 relative overflow-hidden">
        {/* Japanese pattern background */}
        <div className="absolute inset-0 asanoha-pattern"></div>
        <div className="absolute top-0 right-0 w-[500px] h-[500px] opacity-10" style={{ background: `radial-gradient(circle, ${GOLD} 0%, transparent 60%)` }}></div>
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] opacity-10" style={{ background: `radial-gradient(circle, ${GOLD} 0%, transparent 60%)` }}></div>

        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-3 sm:space-y-4 mb-10 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white">
              {t('landing:benefits.title')}
            </h2>
            <p className="text-sm sm:text-lg text-gray-400 max-w-2xl mx-auto px-2">
              {t('landing:benefits.subtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-4 sm:gap-8">
            {/* Principal Protected */}
            <div className="bg-navy-900 p-5 sm:p-8 rounded-2xl border border-gold-300/20 card-glow relative overflow-hidden">
              <div className="absolute -top-10 -right-10 w-32 h-32 opacity-10" style={{ background: `radial-gradient(circle, ${GOLD} 0%, transparent 70%)` }}></div>
              <div className="relative">
                <div className="h-10 w-10 sm:h-14 sm:w-14 bg-gold-300/10 rounded-xl flex items-center justify-center mb-4 sm:mb-6">
                  <Shield className="h-5 w-5 sm:h-7 sm:w-7" style={{ color: GOLD }} />
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-white mb-2 sm:mb-3">{t('landing:benefits.principalProtected.title')}</h3>
                <p className="text-sm sm:text-base text-gray-400 leading-relaxed">
                  {t('landing:benefits.principalProtected.desc')}
                </p>
              </div>
            </div>

            {/* Community Mined */}
            <div className="bg-navy-900 p-5 sm:p-8 rounded-2xl border border-gold-300/20 card-glow relative overflow-hidden">
              <div className="absolute -top-10 -right-10 w-32 h-32 opacity-10" style={{ background: `radial-gradient(circle, ${GOLD} 0%, transparent 70%)` }}></div>
              <div className="relative">
                <div className="h-10 w-10 sm:h-14 sm:w-14 bg-gold-300/10 rounded-xl flex items-center justify-center mb-4 sm:mb-6">
                  <Pickaxe className="h-5 w-5 sm:h-7 sm:w-7" style={{ color: GOLD }} />
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-white mb-2 sm:mb-3">{t('landing:benefits.communityMined.title')}</h3>
                <p className="text-sm sm:text-base text-gray-400 leading-relaxed">
                  {t('landing:benefits.communityMined.desc')}
                </p>
              </div>
            </div>

            {/* Bitcoin Treasury Rewards */}
            <div className="bg-navy-900 p-5 sm:p-8 rounded-2xl border border-gold-300/20 card-glow relative overflow-hidden">
              <div className="absolute -top-10 -right-10 w-32 h-32 opacity-10" style={{ background: `radial-gradient(circle, ${GOLD} 0%, transparent 70%)` }}></div>
              <div className="relative">
                <div className="h-10 w-10 sm:h-14 sm:w-14 bg-gold-300/10 rounded-xl flex items-center justify-center mb-4 sm:mb-6">
                  <Building2 className="h-5 w-5 sm:h-7 sm:w-7" style={{ color: GOLD }} />
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-white mb-2 sm:mb-3">{t('landing:benefits.bitcoinTreasury.title')}</h3>
                <p className="text-sm sm:text-base text-gray-400 leading-relaxed">
                  {t('landing:benefits.bitcoinTreasury.desc')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Powered By Slider */}
      <PoweredBySlider />

      {/* How It Works Section */}
      <section className="py-6 sm:py-10 bg-navy-900 relative overflow-hidden">
        {/* Japanese zen circles background */}
        <div className="absolute inset-0 zen-circles opacity-50"></div>
        {/* Decorative lines */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-1/2 left-0 right-0 h-px" style={{ background: `linear-gradient(90deg, transparent, ${GOLD}, transparent)` }}></div>
          <div className="absolute top-1/3 left-0 right-0 h-px" style={{ background: `linear-gradient(90deg, transparent, ${GOLD}, transparent)` }}></div>
          <div className="absolute top-2/3 left-0 right-0 h-px" style={{ background: `linear-gradient(90deg, transparent, ${GOLD}, transparent)` }}></div>
        </div>
        {/* Corner circles */}
        <div className="absolute -top-20 -left-20 w-80 h-80 rounded-full opacity-5" style={{ background: `radial-gradient(circle, ${GOLD} 0%, transparent 60%)` }}></div>
        <div className="absolute -bottom-20 -right-20 w-80 h-80 rounded-full opacity-5" style={{ background: `radial-gradient(circle, ${GOLD} 0%, transparent 60%)` }}></div>

        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-3 sm:space-y-4 mb-10 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white">
              {t('landing:journey.title')}
            </h2>
            <p className="text-sm sm:text-lg text-gray-400 max-w-2xl mx-auto px-2">
              {t('landing:journey.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-6">
            {[
              { step: 1, title: t('landing:journey.step1Title'), desc: t('landing:journey.step1Desc') },
              { step: 2, title: t('landing:journey.step2Title'), desc: t('landing:journey.step2Desc') },
              { step: 3, title: t('landing:journey.step3Title'), desc: t('landing:journey.step3Desc') },
              { step: 4, title: t('landing:journey.step4Title'), desc: t('landing:journey.step4Desc') },
            ].map((item) => (
              <div key={item.step} className="relative">
                <div className="bg-navy-800 p-4 sm:p-6 rounded-xl border border-gold-300/20 text-center h-full relative overflow-hidden">
                  <div className="absolute -top-6 -right-6 w-24 h-24 opacity-10" style={{ background: `radial-gradient(circle, ${GOLD} 0%, transparent 70%)` }}></div>
                  <div className="relative">
                    <div className="w-9 h-9 sm:w-12 sm:h-12 text-navy-900 rounded-full flex items-center justify-center font-bold text-base sm:text-xl mx-auto mb-3 sm:mb-4" style={{ background: GOLD }}>
                      {item.step}
                    </div>
                    <h3 className="text-sm sm:text-lg font-bold text-white mb-1 sm:mb-2">{item.title}</h3>
                    <p className="text-gray-400 text-xs sm:text-sm">{item.desc}</p>
                  </div>
                </div>
                {item.step < 4 && (
                  <div className="hidden md:block absolute top-1/2 -right-3 transform -translate-y-1/2">
                    <ArrowRight className="h-6 w-6 text-gold-300/40" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tokenomics Section */}
      <section className="py-6 sm:py-10 bg-navy-800 relative overflow-hidden">
        {/* Japanese wave pattern */}
        <div className="absolute inset-0 seigaiha-pattern opacity-30"></div>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 rounded-full blur-[100px]" style={{ background: GOLD }}></div>
          <div className="absolute bottom-20 right-10 w-72 h-72 rounded-full blur-[100px]" style={{ background: GOLD }}></div>
        </div>

        {/* Floating coins decoration */}
        <div className="absolute top-[10%] right-[5%] w-20 h-20 opacity-20 animate-float hidden lg:block" style={{ animationDelay: '0.5s' }}>
          <img src="/images/brand/icon.png" alt="" className="w-full h-full object-contain" />
        </div>
        <div className="absolute bottom-[20%] left-[3%] w-16 h-16 opacity-15 animate-float hidden lg:block" style={{ animationDelay: '1.5s' }}>
          <img src="/images/brand/icon.png" alt="" className="w-full h-full object-contain" />
        </div>

        {/* Floating concentric circles */}
        <div className="absolute top-1/4 left-1/4 w-40 h-40 opacity-10 animate-spin-slow">
          <svg viewBox="0 0 100 100" className="w-full h-full">
            <circle cx="50" cy="50" r="45" fill="none" stroke={GOLD} strokeWidth="0.5" />
            <circle cx="50" cy="50" r="35" fill="none" stroke={GOLD} strokeWidth="0.5" />
            <circle cx="50" cy="50" r="25" fill="none" stroke={GOLD} strokeWidth="0.5" />
          </svg>
        </div>

        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-8 sm:gap-16 items-center">
            {/* Left - Coin Image with animation */}
            <div className="flex justify-center relative order-2 lg:order-1">
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-[200px] sm:w-[350px] h-[200px] sm:h-[350px] rounded-full opacity-30 blur-3xl animate-pulse" style={{ background: GOLD }}></div>
              </div>
              {/* Main coin */}
              <div className="relative animate-float" style={{ animationDuration: '5s' }}>
                <img
                  src="/images/brand/icon.png"
                  alt="$TKR Token"
                  className="w-[180px] sm:w-[320px] h-auto relative z-10 drop-shadow-2xl"
                />
                {/* Orbiting smaller coins */}
                <div className="absolute -top-4 -right-4 w-10 sm:w-16 h-10 sm:h-16 animate-float" style={{ animationDelay: '0.5s', animationDuration: '3s' }}>
                  <img src="/images/brand/icon.png" alt="" className="w-full h-full object-contain opacity-60" />
                </div>
                <div className="absolute -bottom-2 -left-6 w-8 sm:w-12 h-8 sm:h-12 animate-float" style={{ animationDelay: '1s', animationDuration: '4s' }}>
                  <img src="/images/brand/icon.png" alt="" className="w-full h-full object-contain opacity-50" />
                </div>
              </div>
            </div>

            {/* Right - Tokenomics Info */}
            <div className="space-y-6 sm:space-y-8 order-1 lg:order-2">
              <div>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-3 sm:mb-4">
                  {t('landing:tokenomics.title')} <span style={{ color: GOLD }}>{t('landing:tokenomics.titleToken')}</span>
                </h2>
                <p className="text-sm sm:text-lg text-gray-400">
                  {t('landing:tokenomics.subtitle')}
                </p>
              </div>

              <div className="space-y-3 sm:space-y-4">
                <div className="flex justify-between items-center py-3 sm:py-4 border-b border-gold-300/20">
                  <span className="text-sm sm:text-base text-gray-300">{t('landing:tokenomics.totalSupply')}</span>
                  <span style={{ color: GOLD }} className="font-bold text-base sm:text-xl">{t('landing:tokenomics.totalSupplyValue', { supply: config.tokenomics.totalSupply.toLocaleString() })}</span>
                </div>
                <div className="flex justify-between items-center py-3 sm:py-4 border-b border-gold-300/20">
                  <span className="text-sm sm:text-base text-gray-300">{t('landing:tokenomics.distributionMethod')}</span>
                  <span style={{ color: GOLD }} className="font-bold text-base sm:text-xl">{t('landing:tokenomics.distributionValue')}</span>
                </div>
              </div>

              <div className="bg-navy-900 rounded-xl p-4 sm:p-6 border border-gold-300/20">
                <p className="text-sm sm:text-base text-gray-300 leading-relaxed">
                  <strong style={{ color: GOLD }}>{t('landing:tokenomics.utilityLoop')}</strong> {t('landing:tokenomics.utilityDesc')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Three Clickable Blocks - Takara Utility */}
      <section className="py-6 sm:py-10 bg-navy-900 relative overflow-hidden">
        {/* Japanese combined pattern */}
        <div className="absolute inset-0 japan-pattern"></div>
        {/* Floating particles effect */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(12)].map((_, i) => (
            <div
              key={i}
              className="absolute rounded-full opacity-20 animate-pulse"
              style={{
                width: `${8 + (i % 4) * 4}px`,
                height: `${8 + (i % 4) * 4}px`,
                background: `radial-gradient(circle, ${GOLD} 0%, transparent 70%)`,
                top: `${10 + (i * 7) % 80}%`,
                left: `${5 + (i * 8) % 90}%`,
                animationDelay: `${i * 0.3}s`,
                animationDuration: `${2 + (i % 3)}s`
              }}
            />
          ))}
        </div>
        {/* Large decorative circles */}
        <div className="absolute -top-40 -right-40 w-96 h-96 rounded-full opacity-5" style={{ background: `radial-gradient(circle, ${GOLD} 0%, transparent 50%)` }}></div>
        <div className="absolute -bottom-40 -left-40 w-96 h-96 rounded-full opacity-5" style={{ background: `radial-gradient(circle, ${GOLD} 0%, transparent 50%)` }}></div>

        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white">
              {t('landing:utility.title')}
            </h2>
            <p className="text-lg text-gray-400 max-w-3xl mx-auto">
              {t('landing:utility.subtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {/* Block 1 - Stake to Mine */}
            <div
              onClick={() => toggleBlock(1)}
              className="group bg-navy-800 p-8 rounded-2xl border border-gold-300/30 hover:border-gold-300/60 transition-all duration-300 cursor-pointer card-glow relative overflow-hidden"
            >
              <div className="absolute -bottom-10 -right-10 w-40 h-40 opacity-10" style={{ background: `radial-gradient(circle, ${GOLD} 0%, transparent 70%)` }}></div>
              <div className="relative flex flex-col items-center text-center space-y-4">
                <div className="h-16 w-16 bg-gold-300/10 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Pickaxe className="h-8 w-8" style={{ color: GOLD }} />
                </div>
                <h3 className="text-xl font-bold text-white group-hover:text-gold-300 transition-colors">
                  {t('landing:utility.block1.title')}
                </h3>
                <p className="text-gray-400 leading-relaxed text-sm">
                  {t('landing:utility.block1.desc')}
                </p>
                <div className="flex items-center gap-2 font-medium text-sm" style={{ color: GOLD }}>
                  <span>{t('landing:utility.block1.learnMore')}</span>
                  <ChevronDown className={`h-4 w-4 transition-transform ${expandedBlock === 1 ? 'rotate-180' : ''}`} />
                </div>
              </div>

              {/* Expanded Content */}
              <div className={`mt-6 pt-6 border-t border-gold-300/20 overflow-hidden transition-all duration-500 ${expandedBlock === 1 ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'}`}>
                <div className="space-y-4 text-gray-300 text-sm">
                  <p>
                    {t('landing:utility.block1.detail1')}
                  </p>
                  <div className="bg-gold-300/10 border border-gold-300/30 rounded-lg p-4 space-y-2">
                    <div className="flex items-center gap-3">
                      <span style={{ color: GOLD }}>•</span>
                      <span>{t('landing:utility.block1.detail2')}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span style={{ color: GOLD }}>•</span>
                      <span>{t('landing:utility.block1.detail3')}</span>
                    </div>
                  </div>
                  <p className="text-center font-semibold" style={{ color: GOLD }}>
                    {t('landing:utility.block1.summary')}
                  </p>
                </div>
              </div>
            </div>

            {/* Block 2 - Boost APY */}
            <div
              onClick={() => toggleBlock(2)}
              className="group bg-navy-800 p-8 rounded-2xl border border-gold-300/30 hover:border-gold-300/60 transition-all duration-300 cursor-pointer card-glow relative overflow-hidden"
            >
              <div className="absolute -bottom-10 -right-10 w-40 h-40 opacity-10" style={{ background: `radial-gradient(circle, ${GOLD} 0%, transparent 70%)` }}></div>
              <div className="relative flex flex-col items-center text-center space-y-4">
                <div className="h-16 w-16 bg-gold-300/10 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Rocket className="h-8 w-8" style={{ color: GOLD }} />
                </div>
                <h3 className="text-xl font-bold text-white group-hover:text-gold-300 transition-colors">
                  {t('landing:utility.block2.title')}
                </h3>
                <p className="text-gray-400 leading-relaxed text-sm">
                  {t('landing:utility.block2.desc')}
                </p>
                <div className="flex items-center gap-2 font-medium text-sm" style={{ color: GOLD }}>
                  <span>{t('landing:utility.block2.learnMore')}</span>
                  <ChevronDown className={`h-4 w-4 transition-transform ${expandedBlock === 2 ? 'rotate-180' : ''}`} />
                </div>
              </div>

              {/* Expanded Content */}
              <div className={`mt-6 pt-6 border-t border-gold-300/20 overflow-hidden transition-all duration-500 ${expandedBlock === 2 ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'}`}>
                <div className="space-y-4 text-gray-300 text-sm">
                  <p dangerouslySetInnerHTML={{ __html: t('landing:utility.block2.detail1') }} />
                  <div className="bg-gold-300/10 border border-gold-300/30 rounded-lg p-4 space-y-2">
                    <div className="flex items-center gap-3">
                      <span style={{ color: GOLD }}>•</span>
                      <span>{t('landing:utility.block2.detail1')}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span style={{ color: GOLD }}>•</span>
                      <span>{t('landing:utility.block2.detail2')}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span style={{ color: GOLD }}>•</span>
                      <span>{t('landing:utility.block2.detail3')}</span>
                    </div>
                  </div>
                  <p className="text-center font-semibold" style={{ color: GOLD }}>
                    {t('landing:utility.block2.summary')}
                  </p>
                </div>
              </div>
            </div>

            {/* Block 3 - Card Utility */}
            <div
              onClick={() => toggleBlock(3)}
              className="group bg-navy-800 p-8 rounded-2xl border border-gold-300/30 hover:border-gold-300/60 transition-all duration-300 cursor-pointer card-glow relative overflow-hidden"
            >
              <div className="absolute -bottom-10 -right-10 w-40 h-40 opacity-10" style={{ background: `radial-gradient(circle, ${GOLD} 0%, transparent 70%)` }}></div>
              <div className="relative flex flex-col items-center text-center space-y-4">
                <div className="h-16 w-16 bg-gold-300/10 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                  <CreditCard className="h-8 w-8" style={{ color: GOLD }} />
                </div>
                <h3 className="text-xl font-bold text-white group-hover:text-gold-300 transition-colors">
                  {t('landing:utility.block3.title')}
                </h3>
                <p className="text-gray-400 leading-relaxed text-sm">
                  {t('landing:utility.block3.desc')}
                </p>
                <div className="flex items-center gap-2 font-medium text-sm" style={{ color: GOLD }}>
                  <span>{t('landing:utility.block3.learnMore')}</span>
                  <ChevronDown className={`h-4 w-4 transition-transform ${expandedBlock === 3 ? 'rotate-180' : ''}`} />
                </div>
              </div>

              {/* Expanded Content */}
              <div className={`mt-6 pt-6 border-t border-gold-300/20 overflow-hidden transition-all duration-500 ${expandedBlock === 3 ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'}`}>
                <div className="space-y-4 text-gray-300 text-sm">
                  <p>
                    <span style={{ color: GOLD }} className="font-semibold">{t('landing:utility.block3.plannedTitle')}</span>
                  </p>
                  <div className="bg-gold-300/10 border border-gold-300/30 rounded-lg p-4 space-y-2">
                    <div className="flex items-start gap-3">
                      <span style={{ color: GOLD }}>•</span>
                      <span>{t('landing:utility.block3.detail1')}</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <span style={{ color: GOLD }}>•</span>
                      <span>{t('landing:utility.block3.detail2')}</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <span style={{ color: GOLD }}>•</span>
                      <span>{t('landing:utility.block3.detail3')}</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <span style={{ color: GOLD }}>•</span>
                      <span>{t('landing:utility.block3.detail4')}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mastercard + Takara Card Section */}
      <section className="py-6 sm:py-10 bg-navy-800 relative overflow-hidden">
        {/* Japanese asanoha pattern */}
        <div className="absolute inset-0 asanoha-pattern opacity-50"></div>
        <div className="absolute -top-20 -right-20 w-96 h-96 rounded-full opacity-10" style={{ background: `radial-gradient(circle, ${GOLD} 0%, transparent 60%)` }}></div>
        <div className="absolute -bottom-20 -left-20 w-96 h-96 rounded-full opacity-10" style={{ background: `radial-gradient(circle, ${GOLD} 0%, transparent 60%)` }}></div>

        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left: Card Design */}
            <div className="flex justify-center order-2 lg:order-1">
              <div className="relative w-full max-w-md">
                {/* Coming Soon Badge */}
                <span className="absolute -top-3 right-8 z-10 text-navy-900 px-4 py-1.5 rounded-full text-sm font-bold shadow-lg" style={{ background: GOLD }}>
                  {t('landing:card.comingSoon')}
                </span>

                {/* Card Container */}
                <div className="relative w-full aspect-[1.586/1] rounded-2xl shadow-2xl border border-gold-300/20 overflow-hidden"
                     style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)' }}>

                  {/* Background Pattern */}
                  <div className="absolute inset-0 opacity-10">
                    <div className="absolute top-0 right-0 w-64 h-64 rounded-full blur-3xl" style={{ background: GOLD }}></div>
                    <div className="absolute bottom-0 left-0 w-48 h-48 rounded-full blur-2xl" style={{ background: GOLD }}></div>
                  </div>

                  {/* Card Content */}
                  <div className="relative h-full p-6 flex flex-col justify-between">
                    {/* Top Row: Chip + Mastercard Logo */}
                    <div className="flex items-start justify-between">
                      {/* Chip */}
                      <div className="w-12 h-9 rounded-md" style={{ background: 'linear-gradient(135deg, #d4af37 0%, #f5d67a 50%, #d4af37 100%)' }}>
                        <div className="w-full h-full flex items-center justify-center">
                          <div className="w-8 h-6 rounded border border-yellow-900/30 flex items-center">
                            <div className="w-full h-0.5 bg-yellow-900/30"></div>
                          </div>
                        </div>
                      </div>

                      {/* TAKARA + Mastercard Logo */}
                      <div className="flex items-center gap-2">
                        <p className="text-2xl font-bold tracking-wider" style={{ color: 'rgb(239, 206, 146)' }}>TAKARA</p>
                        <div className="flex items-center">
                          <div className="w-8 h-8 rounded-full bg-red-500 opacity-90"></div>
                          <div className="w-8 h-8 rounded-full bg-yellow-500 opacity-90 -ml-3"></div>
                        </div>
                      </div>
                    </div>

                    {/* Card Number */}
                    <div className="mt-6">
                      <p className="text-gray-300 font-mono tracking-widest text-lg sm:text-xl">
                        •••• •••• •••• 1234
                      </p>
                    </div>

                    {/* Bottom Row: Cardholder + Expiry + TAKARA Brand */}
                    <div className="flex items-end justify-between mt-4">
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wider">{t('landing:card.cardHolder')}</p>
                        <p className="font-semibold tracking-wide" style={{ color: GOLD }}>{t('landing:card.cardHolderName')}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-gray-500 uppercase tracking-wider">{t('landing:card.validThru')}</p>
                        <p className="text-gray-300 font-mono">12/28</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right: Content */}
            <div className="space-y-6 order-1 lg:order-2">
              <h2 className="text-3xl sm:text-4xl font-bold text-white">
                {t('landing:card.title')}
              </h2>
              <p className="text-xl text-gray-300 leading-relaxed" dangerouslySetInnerHTML={{ __html: t('landing:card.subtitle') }} />
              <div className="space-y-4">
                <div className="flex items-center gap-4 text-gray-300">
                  <div className="h-10 w-10 bg-gold-300/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <CreditCard className="h-5 w-5" style={{ color: GOLD }} />
                  </div>
                  <span>{t('landing:card.mastercardPartnership')}</span>
                </div>
                <div className="flex items-center gap-4 text-gray-300">
                  <div className="h-10 w-10 bg-gold-300/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Zap className="h-5 w-5" style={{ color: GOLD }} />
                  </div>
                  <span>{t('landing:card.instantConversion')}</span>
                </div>
                <div className="flex items-center gap-4 text-gray-300">
                  <div className="h-10 w-10 bg-gold-300/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Shield className="h-5 w-5" style={{ color: GOLD }} />
                  </div>
                  <span>{t('landing:card.securePayments')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bitcoin Vault Section */}
      <section className="py-6 sm:py-10 bg-navy-900 relative overflow-hidden">
        {/* Japanese zen circles background */}
        <div className="absolute inset-0 zen-circles opacity-50"></div>
        <div className="absolute top-10 right-10 w-60 h-60 opacity-10" style={{ background: `radial-gradient(circle, #F7931A 0%, transparent 60%)` }}></div>
        <div className="absolute bottom-10 left-10 w-60 h-60 opacity-10" style={{ background: `radial-gradient(circle, #F7931A 0%, transparent 60%)` }}></div>

        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left: Content */}
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <div className="h-14 w-14 bg-orange-500/20 rounded-xl flex items-center justify-center">
                  <Bitcoin className="h-8 w-8 text-orange-500" />
                </div>
                <h2 className="text-3xl sm:text-4xl font-bold text-white">
                  {t('landing:bitcoinVault.title')}
                </h2>
              </div>
              <p className="text-xl text-gray-300 leading-relaxed" dangerouslySetInnerHTML={{ __html: t('landing:bitcoinVault.subtitle', { percent: config.tokenomics.bitcoinVaultUnlockPercent }) }} />
              <p className="text-gray-400 leading-relaxed">
                {t('landing:bitcoinVault.desc')}
              </p>
              <div className="bg-navy-800 p-5 rounded-xl border border-orange-500/20">
                <div className="flex justify-between items-center mb-3">
                  <span className="text-gray-300 font-medium">{t('landing:bitcoinVault.progressTitle')}</span>
                  <span className="text-orange-400 font-bold">{miningProgress.toFixed(2)}% / {config.tokenomics.bitcoinVaultUnlockPercent}%</span>
                </div>
                <div className="bg-navy-900 rounded-full h-3 overflow-hidden">
                  <div
                    className="bg-gradient-to-r from-orange-600 to-yellow-500 h-3 rounded-full transition-all duration-500"
                    style={{ width: `${bitcoinVaultProgress}%` }}
                  ></div>
                </div>
                <p className="text-xs text-gray-500 mt-2">{t('landing:bitcoinVault.progressRequired', { amount: (config.tokenomics.totalSupply * config.tokenomics.bitcoinVaultUnlockPercent / 100).toLocaleString() })}</p>
              </div>
            </div>

            {/* Right: Bitcoin Icon */}
            <div className="flex justify-center">
              <div className="relative">
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-72 h-72 rounded-full opacity-20 blur-3xl animate-pulse" style={{ background: '#F7931A' }}></div>
                </div>
                <div className="relative w-64 h-64 bg-gradient-to-br from-orange-500/30 to-yellow-500/30 rounded-full flex items-center justify-center border border-orange-500/20 animate-float" style={{ animationDuration: '6s' }}>
                  <Bitcoin className="w-32 h-32 text-orange-500" />
                  <div className="absolute -top-2 -right-2 w-8 h-8 bg-orange-500/40 rounded-full animate-ping"></div>
                  <div className="absolute -bottom-2 -left-2 w-6 h-6 bg-yellow-500/40 rounded-full animate-ping" style={{ animationDelay: '0.5s' }}></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Treasury Vaults Section - v3.0: Treasury Vaults */}
      <section className="py-6 sm:py-10 bg-navy-800 relative overflow-hidden">
        {/* Japanese asanoha pattern */}
        <div className="absolute inset-0 asanoha-pattern opacity-50"></div>
        {/* Background decoration */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-1/4 w-px h-full" style={{ background: `linear-gradient(180deg, transparent, ${GOLD}, transparent)` }}></div>
          <div className="absolute top-0 left-1/2 w-px h-full" style={{ background: `linear-gradient(180deg, transparent, ${GOLD}, transparent)` }}></div>
          <div className="absolute top-0 left-3/4 w-px h-full" style={{ background: `linear-gradient(180deg, transparent, ${GOLD}, transparent)` }}></div>
        </div>
        {/* Concentric circles decoration */}
        <div className="absolute top-10 right-10 w-60 h-60 opacity-5">
          <svg viewBox="0 0 100 100" className="w-full h-full">
            <circle cx="50" cy="50" r="48" fill="none" stroke={GOLD} strokeWidth="0.3" />
            <circle cx="50" cy="50" r="40" fill="none" stroke={GOLD} strokeWidth="0.3" />
            <circle cx="50" cy="50" r="32" fill="none" stroke={GOLD} strokeWidth="0.3" />
            <circle cx="50" cy="50" r="24" fill="none" stroke={GOLD} strokeWidth="0.3" />
          </svg>
        </div>
        <div className="absolute bottom-10 left-10 w-60 h-60 opacity-5">
          <svg viewBox="0 0 100 100" className="w-full h-full">
            <circle cx="50" cy="50" r="48" fill="none" stroke={GOLD} strokeWidth="0.3" />
            <circle cx="50" cy="50" r="40" fill="none" stroke={GOLD} strokeWidth="0.3" />
            <circle cx="50" cy="50" r="32" fill="none" stroke={GOLD} strokeWidth="0.3" />
            <circle cx="50" cy="50" r="24" fill="none" stroke={GOLD} strokeWidth="0.3" />
          </svg>
        </div>

        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white">
              {t('landing:treasuryVaults.title')}
            </h2>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto">
              {t('landing:treasuryVaults.subtitle')}
            </p>
          </div>

          {vaultsLoading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin" style={{ color: GOLD }} />
            </div>
          ) : (
            <div className="max-w-4xl mx-auto mb-12">
              {/* Treasury Vaults Card */}
              <div className="bg-navy-900 p-8 rounded-2xl border border-gold-300/30 relative overflow-hidden">
                <div className="absolute -top-20 -right-20 w-40 h-40 rounded-full opacity-10 blur-3xl" style={{ background: GOLD }}></div>

                <div className="relative">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="tier-elite inline-block px-4 py-2 rounded-full text-lg font-bold">TREASURY</div>
                    <h3 className="text-2xl font-bold text-white">
                      {t('landing:treasuryVaults.from', { amount: treasuryStats?.minDeposit?.toLocaleString() || '300' })}
                    </h3>
                  </div>

                  <div className="grid md:grid-cols-2 gap-8">
                    {/* USDT Returns */}
                    <div className="space-y-4">
                      <h4 className="text-lg font-semibold text-teal-400">{t('landing:treasuryVaults.usdtReturns')}</h4>
                      <div className="space-y-3 text-gray-300">
                        <p className="flex items-center gap-2">
                          <span style={{ color: GOLD }}>✓</span> {t('landing:treasuryVaults.usdtAPY', { apy: treasuryStats?.maxBaseAPY?.toFixed(1) || '19.2' })}
                        </p>
                        <p className="flex items-center gap-2">
                          <span style={{ color: GOLD }}>✓</span> {t('landing:treasuryVaults.totalReturns', { returns: treasuryStats?.maxTotalReturns || 58 })}
                        </p>
                        <p className="flex items-center gap-2">
                          <span style={{ color: GOLD }}>✓</span> {t('landing:treasuryVaults.monthlyPayouts')}
                        </p>
                      </div>
                    </div>

                    {/* TAKARA Mining */}
                    <div className="space-y-4">
                      <h4 className="text-lg font-semibold text-green-400">{t('landing:treasuryVaults.takaraMining')}</h4>
                      <div className="space-y-3 text-gray-300">
                        <p className="flex items-center gap-2">
                          <span className="text-green-400">⚡</span> {t('landing:treasuryVaults.dynamicAPY')}
                        </p>
                        <p className="flex items-center gap-2">
                          <span className="text-green-400">⚡</span> {t('landing:treasuryVaults.communityMined')}
                        </p>
                        <p className="flex items-center gap-2">
                          <span className="text-green-400">⚡</span> {t('landing:treasuryVaults.noPremine')}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Vault Durations */}
                  <div className="mt-8 pt-6 border-t border-gold-300/20">
                    <p className="text-sm text-gray-400 mb-3">{t('landing:treasuryVaults.lockupPeriods')}</p>
                    <div className="flex flex-wrap gap-3">
                      {(treasuryStats?.durations || [18, 20, 30, 36]).map((duration: number) => (
                        <span
                          key={duration}
                          className="px-4 py-2 rounded-lg border border-gold-300/30 text-gold-400 font-semibold"
                        >
                          {t('landing:treasuryVaults.months', { duration })}
                        </span>
                      ))}
                      <span className="px-4 py-2 rounded-lg border border-orange-500/30 text-orange-400 font-semibold">
                        {t('landing:treasuryVaults.btcComingSoon')}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="text-center">
            <Link
              to="/app/vaults"
              className="btn-gold inline-flex items-center gap-2 px-8 py-4 rounded-lg font-semibold text-lg"
            >
              {t('landing:treasuryVaults.viewAllVaults')}
              <ArrowRight className="h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Treasury & Roadmap */}
      <section className="py-6 sm:py-10 bg-navy-900 relative overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full opacity-5" style={{ background: `radial-gradient(circle, ${GOLD} 0%, transparent 50%)` }}></div>

        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Treasury */}
            <div className="bg-navy-800 p-8 rounded-2xl border border-gold-300/20 relative overflow-hidden">
              <div className="absolute -top-10 -left-10 w-32 h-32 opacity-10" style={{ background: `radial-gradient(circle, ${GOLD} 0%, transparent 70%)` }}></div>
              <div className="relative">
                <div className="flex items-center gap-3 mb-6">
                  <Coins className="h-8 w-8" style={{ color: GOLD }} />
                  <h3 className="text-2xl font-bold text-white">{t('landing:treasuryRoadmap.treasuryTitle')}</h3>
                </div>
                <p className="text-gray-300 mb-6 leading-relaxed" dangerouslySetInnerHTML={{ __html: t('landing:treasuryRoadmap.treasuryDesc') }} />
                <div className="bg-navy-900 rounded-xl p-6 border border-gold-300/10">
                  <h4 style={{ color: GOLD }} className="font-semibold mb-2">{t('landing:treasuryRoadmap.futureTitle')}</h4>
                  <p className="text-gray-400">
                    {t('landing:treasuryRoadmap.futureDesc')}
                  </p>
                </div>
              </div>
            </div>

            {/* Wexel NFT */}
            <div className="bg-navy-800 p-8 rounded-2xl border border-gold-300/20 relative overflow-hidden">
              <div className="absolute -top-10 -right-10 w-32 h-32 opacity-10" style={{ background: `radial-gradient(circle, ${GOLD} 0%, transparent 70%)` }}></div>
              <div className="relative">
                <div className="flex items-center gap-3 mb-6">
                  <Zap className="h-8 w-8" style={{ color: GOLD }} />
                  <h3 className="text-2xl font-bold text-white">{t('landing:treasuryRoadmap.wexelTitle')}</h3>
                </div>
                <p className="text-gray-300 leading-relaxed" dangerouslySetInnerHTML={{ __html: t('landing:treasuryRoadmap.wexelDesc') }} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-6 sm:py-10 bg-gradient-to-b from-navy-800 to-navy-900 relative overflow-hidden">
        {/* Japanese patterns */}
        <div className="absolute inset-0 zen-circles opacity-30"></div>
        <div className="absolute inset-0 seigaiha-pattern opacity-20"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full opacity-20 animate-pulse" style={{ background: `radial-gradient(circle, ${GOLD} 0%, transparent 40%)` }}></div>

        {/* Gold Tree on left */}
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] opacity-15 hidden lg:block">
          <img src="/images/brand/gold-tree.png" alt="" className="w-full h-full object-contain" />
        </div>

        {/* Floating coins */}
        <div className="absolute top-[15%] right-[10%] w-20 h-20 opacity-30 animate-float hidden md:block" style={{ animationDuration: '5s' }}>
          <img src="/images/brand/gold-coin.png" alt="" className="w-full h-full object-contain" />
        </div>
        <div className="absolute top-[40%] left-[5%] w-12 h-12 opacity-20 animate-float hidden lg:block" style={{ animationDelay: '2s', animationDuration: '7s' }}>
          <img src="/images/brand/gold-coin.png" alt="" className="w-full h-full object-contain" />
        </div>

        {/* Abstract shape */}
        <div className="absolute top-10 right-10 w-32 h-32 opacity-10 animate-spin-slow hidden lg:block">
          <img src="/images/brand/gold-abstract.png" alt="" className="w-full h-full object-contain" />
        </div>

        {/* Animated concentric circles */}
        <div className="absolute top-20 left-20 w-40 h-40 opacity-15 animate-spin-slow" style={{ animationDuration: '30s' }}>
          <svg viewBox="0 0 100 100" className="w-full h-full">
            <circle cx="50" cy="50" r="45" fill="none" stroke={GOLD} strokeWidth="0.5" />
            <circle cx="50" cy="50" r="35" fill="none" stroke={GOLD} strokeWidth="0.5" />
            <circle cx="50" cy="50" r="25" fill="none" stroke={GOLD} strokeWidth="0.5" />
          </svg>
        </div>
        <div className="absolute bottom-20 right-20 w-40 h-40 opacity-15 animate-spin-slow" style={{ animationDuration: '25s', animationDirection: 'reverse' }}>
          <svg viewBox="0 0 100 100" className="w-full h-full">
            <circle cx="50" cy="50" r="45" fill="none" stroke={GOLD} strokeWidth="0.5" />
            <circle cx="50" cy="50" r="35" fill="none" stroke={GOLD} strokeWidth="0.5" />
            <circle cx="50" cy="50" r="25" fill="none" stroke={GOLD} strokeWidth="0.5" />
          </svg>
        </div>

        {/* Floating particles */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(10)].map((_, i) => (
            <div
              key={`cta-particle-${i}`}
              className="absolute rounded-full animate-float-particle"
              style={{
                width: `${3 + (i % 4) * 2}px`,
                height: `${3 + (i % 4) * 2}px`,
                background: GOLD,
                opacity: 0.4,
                top: `${10 + (i * 8) % 80}%`,
                left: `${5 + (i * 10) % 90}%`,
                animationDelay: `${i * 0.5}s`,
                animationDuration: `${5 + (i % 3) * 2}s`
              }}
            />
          ))}
        </div>

        <div className="relative mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center space-y-8">
          <div className="animate-float" style={{ animationDuration: '6s' }}>
            <img
              src="/images/brand/logo-takara.png"
              alt="Takara"
              className="h-16 md:h-20 w-auto mx-auto mb-4 drop-shadow-lg"
            />
          </div>
          <h2 className="text-3xl sm:text-5xl font-bold text-white leading-tight">
            {t('landing:finalCTA.title')}
          </h2>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            {t('landing:finalCTA.subtitle')}
          </p>
          <div className="pt-4">
            <Link
              to="/app/vaults"
              className="btn-gold inline-flex items-center gap-2 px-10 py-4 rounded-lg font-semibold text-lg animate-pulse-glow"
            >
              {t('landing:finalCTA.cta')}
              <ArrowRight className="h-6 w-6" />
            </Link>
          </div>
        </div>
      </section>

      {/* Information Blocks Section */}
      <section className="py-6 sm:py-10 bg-navy-800 relative overflow-hidden">
        <div className="absolute inset-0 asanoha-pattern opacity-30"></div>
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {/* About Takara */}
            <div className="bg-navy-900 p-6 sm:p-8 rounded-xl border border-gold-300/20 card-glow">
              <div className="h-12 w-12 sm:h-14 sm:w-14 bg-gold-300/10 rounded-xl flex items-center justify-center mb-4">
                <Info className="h-6 w-6 sm:h-7 sm:w-7" style={{ color: GOLD }} />
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-white mb-3">{t('landing:infoBlocks.aboutTakara.title')}</h3>
              {expandedInfoCards.has(0) ? (
                <>
                  <p className="text-sm sm:text-base text-gray-300 leading-relaxed" dangerouslySetInnerHTML={{ __html: t('landing:infoBlocks.aboutTakara.full') }} />
                  <button
                    onClick={() => toggleInfoCard(0)}
                    className="mt-4 text-sm font-medium flex items-center gap-1 transition-colors"
                    style={{ color: GOLD }}
                  >
                    {t('landing:infoBlocks.aboutTakara.showLess')}
                    <ChevronDown className="h-4 w-4 rotate-180 transition-transform" />
                  </button>
                </>
              ) : (
                <button
                  onClick={() => toggleInfoCard(0)}
                  className="text-sm font-medium flex items-center gap-1 transition-colors"
                  style={{ color: GOLD }}
                >
                  {t('landing:infoBlocks.aboutTakara.readMore')}
                  <ChevronDown className="h-4 w-4 transition-transform" />
                </button>
              )}
            </div>

            {/* Banking & Financial Services */}
            <div className="bg-navy-900 p-6 sm:p-8 rounded-xl border border-gold-300/20 card-glow">
              <div className="h-12 w-12 sm:h-14 sm:w-14 bg-gold-300/10 rounded-xl flex items-center justify-center mb-4">
                <Building2 className="h-6 w-6 sm:h-7 sm:w-7" style={{ color: GOLD }} />
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-white mb-3">{t('landing:infoBlocks.banking.title')}</h3>
              {expandedInfoCards.has(1) ? (
                <>
                  <p className="text-sm sm:text-base text-gray-300 leading-relaxed" dangerouslySetInnerHTML={{ __html: t('landing:infoBlocks.banking.full') }} />
                  <button
                    onClick={() => toggleInfoCard(1)}
                    className="mt-4 text-sm font-medium flex items-center gap-1 transition-colors"
                    style={{ color: GOLD }}
                  >
                    {t('landing:infoBlocks.aboutTakara.showLess')}
                    <ChevronDown className="h-4 w-4 rotate-180 transition-transform" />
                  </button>
                </>
              ) : (
                <button
                  onClick={() => toggleInfoCard(1)}
                  className="text-sm font-medium flex items-center gap-1 transition-colors"
                  style={{ color: GOLD }}
                >
                  {t('landing:infoBlocks.aboutTakara.readMore')}
                  <ChevronDown className="h-4 w-4 transition-transform" />
                </button>
              )}
            </div>

            {/* Digital Assets & Yield Model */}
            <div className="bg-navy-900 p-6 sm:p-8 rounded-xl border border-gold-300/20 card-glow">
              <div className="h-12 w-12 sm:h-14 sm:w-14 bg-gold-300/10 rounded-xl flex items-center justify-center mb-4">
                <Coins className="h-6 w-6 sm:h-7 sm:w-7" style={{ color: GOLD }} />
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-white mb-3">{t('landing:infoBlocks.digitalAssets.title')}</h3>
              {expandedInfoCards.has(2) ? (
                <>
                  <p className="text-sm sm:text-base text-gray-300 leading-relaxed" dangerouslySetInnerHTML={{ __html: t('landing:infoBlocks.digitalAssets.full') }} />
                  <button
                    onClick={() => toggleInfoCard(2)}
                    className="mt-4 text-sm font-medium flex items-center gap-1 transition-colors"
                    style={{ color: GOLD }}
                  >
                    {t('landing:infoBlocks.aboutTakara.showLess')}
                    <ChevronDown className="h-4 w-4 rotate-180 transition-transform" />
                  </button>
                </>
              ) : (
                <button
                  onClick={() => toggleInfoCard(2)}
                  className="text-sm font-medium flex items-center gap-1 transition-colors"
                  style={{ color: GOLD }}
                >
                  {t('landing:infoBlocks.aboutTakara.readMore')}
                  <ChevronDown className="h-4 w-4 transition-transform" />
                </button>
              )}
            </div>

            {/* Cards & Payment Services */}
            <div className="bg-navy-900 p-6 sm:p-8 rounded-xl border border-gold-300/20 card-glow">
              <div className="h-12 w-12 sm:h-14 sm:w-14 bg-gold-300/10 rounded-xl flex items-center justify-center mb-4">
                <CreditCard className="h-6 w-6 sm:h-7 sm:w-7" style={{ color: GOLD }} />
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-white mb-3">{t('landing:infoBlocks.cards.title')}</h3>
              {expandedInfoCards.has(3) ? (
                <>
                  <p className="text-sm sm:text-base text-gray-300 leading-relaxed" dangerouslySetInnerHTML={{ __html: t('landing:infoBlocks.cards.full') }} />
                  <button
                    onClick={() => toggleInfoCard(3)}
                    className="mt-4 text-sm font-medium flex items-center gap-1 transition-colors"
                    style={{ color: GOLD }}
                  >
                    {t('landing:infoBlocks.aboutTakara.showLess')}
                    <ChevronDown className="h-4 w-4 rotate-180 transition-transform" />
                  </button>
                </>
              ) : (
                <button
                  onClick={() => toggleInfoCard(3)}
                  className="text-sm font-medium flex items-center gap-1 transition-colors"
                  style={{ color: GOLD }}
                >
                  {t('landing:infoBlocks.aboutTakara.readMore')}
                  <ChevronDown className="h-4 w-4 transition-transform" />
                </button>
              )}
            </div>

            {/* Security & Compliance */}
            <div className="bg-navy-900 p-6 sm:p-8 rounded-xl border border-gold-300/20 card-glow">
              <div className="h-12 w-12 sm:h-14 sm:w-14 bg-gold-300/10 rounded-xl flex items-center justify-center mb-4">
                <Shield className="h-6 w-6 sm:h-7 sm:w-7" style={{ color: GOLD }} />
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-white mb-3">{t('landing:infoBlocks.security.title')}</h3>
              {expandedInfoCards.has(4) ? (
                <>
                  <p className="text-sm sm:text-base text-gray-300 leading-relaxed" dangerouslySetInnerHTML={{ __html: t('landing:infoBlocks.security.full') }} />
                  <button
                    onClick={() => toggleInfoCard(4)}
                    className="mt-4 text-sm font-medium flex items-center gap-1 transition-colors"
                    style={{ color: GOLD }}
                  >
                    {t('landing:infoBlocks.aboutTakara.showLess')}
                    <ChevronDown className="h-4 w-4 rotate-180 transition-transform" />
                  </button>
                </>
              ) : (
                <button
                  onClick={() => toggleInfoCard(4)}
                  className="text-sm font-medium flex items-center gap-1 transition-colors"
                  style={{ color: GOLD }}
                >
                  {t('landing:infoBlocks.aboutTakara.readMore')}
                  <ChevronDown className="h-4 w-4 transition-transform" />
                </button>
              )}
            </div>

            {/* Technology & Infrastructure */}
            <div className="bg-navy-900 p-6 sm:p-8 rounded-xl border border-gold-300/20 card-glow">
              <div className="h-12 w-12 sm:h-14 sm:w-14 bg-gold-300/10 rounded-xl flex items-center justify-center mb-4">
                <Layers className="h-6 w-6 sm:h-7 sm:w-7" style={{ color: GOLD }} />
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-white mb-3">{t('landing:infoBlocks.technology.title')}</h3>
              {expandedInfoCards.has(5) ? (
                <>
                  <p className="text-sm sm:text-base text-gray-300 leading-relaxed" dangerouslySetInnerHTML={{ __html: t('landing:infoBlocks.technology.full') }} />
                  <button
                    onClick={() => toggleInfoCard(5)}
                    className="mt-4 text-sm font-medium flex items-center gap-1 transition-colors"
                    style={{ color: GOLD }}
                  >
                    {t('landing:infoBlocks.aboutTakara.showLess')}
                    <ChevronDown className="h-4 w-4 rotate-180 transition-transform" />
                  </button>
                </>
              ) : (
                <button
                  onClick={() => toggleInfoCard(5)}
                  className="text-sm font-medium flex items-center gap-1 transition-colors"
                  style={{ color: GOLD }}
                >
                  {t('landing:infoBlocks.aboutTakara.readMore')}
                  <ChevronDown className="h-4 w-4 transition-transform" />
                </button>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
